import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Chip,
  Paper,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HeroSection from '../components/common/HeroSection';
import SectionTitle from '../components/common/SectionTitle';
import DownloadIcon from '@mui/icons-material/Download';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { courseScheduleData } from '../data/schedule';

const dayColors = {
  Monday: '#E91E63',
  Tuesday: '#9C27B0',
  Wednesday: '#3F51B5',
  Thursday: '#00BCD4',
  Friday: '#4CAF50',
  Saturday: '#FF9800',
  Sunday: '#F44336',
};

const SchedulePage = () => {
  const [filter, setFilter] = useState('all');

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const filteredData =
    filter === 'all'
      ? courseScheduleData
      : courseScheduleData.filter((item) => item.level === filter);

  return (
    <Box>
      <HeroSection
        title="2027 Course Schedule"
        subtitle="Find the perfect class time for your child"
      />

      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <SectionTitle
            title="Class Timetable"
            subtitle="Select a level to filter the schedule"
          />

          {/* Filter */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={handleFilterChange}
              sx={{
                bgcolor: '#fff',
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                '& .MuiToggleButton-root': {
                  px: { xs: 2, md: 4 },
                  py: 1.5,
                  border: 'none',
                  borderRadius: '12px !important',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: '#fff',
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                },
              }}
            >
              <ToggleButton value="all">All Levels</ToggleButton>
              <ToggleButton value="primary">Primary</ToggleButton>
              <ToggleButton value="secondary">Secondary</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Schedule Cards */}
          <Grid container spacing={3}>
            {filteredData.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.grade}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `4px solid ${item.level === 'primary' ? '#FF6F00' : '#26A69A'}`,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {item.grade}
                      </Typography>
                      <Chip
                        label={item.level === 'primary' ? 'Primary' : 'Secondary'}
                        size="small"
                        sx={{
                          bgcolor: item.level === 'primary' ? 'rgba(255,111,0,0.1)' : 'rgba(38,166,154,0.1)',
                          color: item.level === 'primary' ? 'primary.main' : 'secondary.main',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', mb: 2 }}
                    >
                      {item.gradeZh}
                    </Typography>

                    {item.classes.map((cls, idx) => (
                      <Paper
                        key={idx}
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: 1.5,
                          bgcolor: 'rgba(0,0,0,0.02)',
                          borderRadius: 2,
                          borderLeft: `3px solid ${dayColors[cls.day] || '#999'}`,
                        }}
                      >
                        {cls.name && (
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {cls.name} ({cls.nameZh})
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <CalendarTodayIcon sx={{ fontSize: 16, color: dayColors[cls.day] || '#999' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {cls.day} ({cls.dayZh})
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {cls.time}
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mt: 6,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              href={process.env.PUBLIC_URL + '/course-schedule.pdf'}
              target="_blank"
              download
            >
              Download PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<ContactMailIcon />}
              component={RouterLink}
              to="/contact"
            >
              Enroll Now
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default SchedulePage;
